import { useEffect, useSyncExternalStore } from 'react';

/**
 * Reclamo del aviso de offline. El pill "Sin conexión" de `PwaLayer` es
 * app-wide; cuando una pantalla ya muestra su propio cartel de conexión (hoy:
 * `ConnectionBanner` en la charla) los dos quedaban uno arriba del otro
 * diciendo lo mismo. La pantalla reclama el lugar con
 * `useClaimOfflineBanner(true)` mientras su cartel está visible, y `PwaLayer`
 * esconde el pill mientras haya un reclamo vivo.
 *
 * Es un contador (no un boolean) para que dos reclamantes solapados no se
 * destraben entre sí; y va atado a la VISIBILIDAD del cartel, no a la ruta:
 * mientras el WS todavía no notó la caída el pill sigue cubriendo.
 */
let claims = 0;
const listeners = new Set<() => void>();

function emit(): void {
  for (const listener of listeners) listener();
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

function isClaimed(): boolean {
  return claims > 0;
}

/** Mientras `active`, el aviso slim de offline de `PwaLayer` no se muestra. */
export function useClaimOfflineBanner(active: boolean): void {
  useEffect(() => {
    if (!active) return;
    claims += 1;
    emit();
    return () => {
      claims -= 1;
      emit();
    };
  }, [active]);
}

/** true mientras alguna pantalla tenga reclamado el aviso (lo lee `PwaLayer`). */
export function useOfflineBannerClaimed(): boolean {
  return useSyncExternalStore(subscribe, isClaimed, () => false);
}
